import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Feather, Mail, Lock, User, ArrowLeft } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
      navigate('/subscription');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-lora">Back to home</span>
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <Feather className="w-8 h-8 text-wine" />
            <span className="font-playfair text-2xl text-charcoal">Unspoken Hearts</span>
          </div>

          <h1 className="font-playfair text-4xl text-charcoal mb-2">Begin Your Journey</h1>
          <p className="font-lora text-charcoal-muted mb-8">Create an account to access all letters.</p>

          <Card className="card-letter">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-lora text-charcoal">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-underline pl-10 py-3"
                    placeholder="Your name"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="font-lora text-charcoal">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-underline pl-10 py-3"
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-lora text-charcoal">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-underline pl-10 py-3"
                    placeholder="••••••••"
                    minLength={6}
                    required
                    data-testid="password-input"
                  />
                </div>
                <p className="font-lora text-xs text-charcoal-light">Minimum 6 characters</p>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full justify-center"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </Card>

          <p className="text-center mt-6 font-lora text-charcoal-muted">
            Already have an account?{' '}
            <Link to="/login" className="text-sepia hover:text-wine transition-colors" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1694285453486-ead6e637e524?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwbG92ZSUyMGxldHRlciUyMHBhcGVyJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzI2NzY5MDF8MA&ixlib=rb-4.1.0&q=85"
          alt="Envelope with leaf"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal/20"></div>
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="font-playfair text-2xl text-parchment italic">
            "Every love story is beautiful, but ours is my favorite."
          </blockquote>
          <p className="font-dancing text-xl text-parchment/80 mt-4">— Unknown, but timeless</p>
        </div>
      </div>
    </div>
  );
}
